import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, ArrowRight, Lock, Sparkles } from "lucide-react";
import { InfraVestLogo } from "@/components/InfraVestLogo";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/0d225e69-bbdf-46e2-9a5f-5e718f2e6f47/images/6f1c54c3b633d211b930325e555e84840fb05269af4ee52ac27f6c2c8adb4258.png";

const isValidEmail = (e) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(String(e || "").trim());

const SamplePage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Enter a valid email", {
        description: "We need a working address to send investor updates.",
      });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setJoined(true);
      toast.success("You're on the list", {
        description: `We'll reach ${email} with early access.`,
      });
      setEmail("");
    }, 700);
  };

  return (
    <main
      data-testid="sample-page"
      className="relative isolate min-h-screen overflow-hidden bg-black text-white"
    >
      {/* Background: cell tower with Ken Burns + overlays */}
      <img
        src={HERO_IMAGE}
        alt="Cell tower at sunset"
        className="kenburns absolute inset-0 h-full w-full object-cover"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_15%,rgba(249,115,22,0.22),transparent_55%)]" />
      <div className="grain pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute inset-0 overflow-hidden mix-blend-screen">
        <div className="scanline absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-orange-500/10 to-transparent" />
      </div>

      {/* Top bar */}
      <header className="relative z-10">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <InfraVestLogo size={26} />
          <a
            href="/full"
            data-testid="sample-nav-full-site"
            aria-label="Full site"
            title=""
            className="text-xs uppercase tracking-[0.22em] text-neutral-500 no-underline transition-colors hover:text-neutral-500"
          >
            &nbsp;
          </a>
        </div>
      </header>

      {/* Centerpiece */}
      <section className="relative z-10 flex min-h-[calc(100svh-4rem)] items-center">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-14 px-6 py-16 lg:grid-cols-12">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <p
              data-testid="sample-eyebrow"
              className="rise inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.22em] text-orange-300"
            >
              <Sparkles className="h-3.5 w-3.5" /> Early Access · Private list
            </p>

            <h1
              data-testid="sample-headline"
              className="rise rise-delay-1 mt-6 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl"
            >
              Infrastructure
              <br />
              ownership is{" "}
              <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-yellow-400 bg-clip-text text-transparent">
                evolving.
              </span>
            </h1>

            <p
              data-testid="sample-subcopy"
              className="rise rise-delay-2 mt-7 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
            >
              Join the early list for investor updates, owner opportunities,
              and platform access. No spam. Unsubscribe in one click.
            </p>

            {/* Trust row */}
            <div className="rise rise-delay-3 mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-neutral-500">
              <span className="inline-flex items-center gap-2">
                <Lock className="h-3.5 w-3.5 text-orange-400" />
                Investor-only
              </span>
              <span>· Towers</span>
              <span>· Fiber</span>
              <span>· Edge compute</span>
            </div>
          </div>

          {/* Right: signup card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-neutral-900/80 to-neutral-950/80 p-1 backdrop-blur-xl">
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[conic-gradient(from_120deg,rgba(249,115,22,0.4),transparent_40%,rgba(234,179,8,0.35),transparent_70%,rgba(249,115,22,0.4))] opacity-50 blur-[2px]" />
              <div className="relative rounded-[22px] bg-neutral-950/85 p-8 backdrop-blur-xl sm:p-10">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-2xl font-semibold text-white">
                    Join Early Access
                  </h2>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    IV · 2026
                  </span>
                </div>
                <p className="mt-1 text-sm text-neutral-400">
                  Be first when the grid opens up.
                </p>

                <form
                  onSubmit={onSubmit}
                  noValidate
                  className="mt-7 space-y-4"
                  data-testid="sample-form"
                >
                  <label
                    htmlFor="sample-email"
                    className="block text-xs font-medium uppercase tracking-[0.18em] text-neutral-400"
                  >
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                    <Input
                      id="sample-email"
                      data-testid="sample-email-input"
                      type="email"
                      placeholder="you@firm.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="gv-input h-14 rounded-2xl border-white/10 bg-neutral-900/70 pl-11 pr-4 text-base text-white placeholder:text-neutral-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="sample-submit"
                    className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 text-sm font-semibold text-neutral-950 transition-all duration-300 hover:from-orange-400 hover:to-yellow-300 hover:shadow-[0_18px_60px_-12px_rgba(249,115,22,0.6)] disabled:opacity-70"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-neutral-950 border-t-transparent" />
                        Submitting…
                      </span>
                    ) : (
                      <>
                        Join Early Access
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  {joined && (
                    <div
                      data-testid="sample-success"
                      className="rounded-xl border border-orange-500/20 bg-orange-500/5 px-4 py-3 text-sm text-orange-200"
                    >
                      You're on the list. Check your inbox soon.
                    </div>
                  )}

                  <p
                    data-testid="sample-disclaimer"
                    className="pt-2 text-[11px] leading-relaxed text-neutral-500"
                  >
                    Investment opportunities, if offered, will be made only
                    through compliant securities offerings and official
                    offering documents.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="relative z-10 border-t border-white/10 bg-black/60 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-2 px-6 py-5 text-xs text-neutral-500 sm:flex-row sm:items-center">
          <div>© {new Date().getFullYear()} InfraVest, Inc.</div>
          <a
            href="/full"
            data-testid="footer-sample-link"
            aria-label="Sample"
            className="cursor-pointer font-mono uppercase tracking-[0.22em] text-neutral-500 no-underline transition-colors hover:text-neutral-500"
          >
            v0.1 · Sample
          </a>
        </div>
      </footer>
    </main>
  );
};

export default SamplePage;
